import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from "@angular/common/http";
import { Injectable, Injector, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable, catchError, combineLatest, first, switchMap, throwError } from "rxjs";
import { RootState } from "src/app/store";
import * as FromUser from "src/app/store/user/selectors";
import * as FromRouter from "src/app/store/router/selectors";
import * as UserActions from "src/app/store/user/actions";
import { NavController } from "@ionic/angular";
import { AppRoutes } from "src/app/enums";
import { jwtDecode } from "jwt-decode";
import * as Sentry from "@sentry/capacitor";
import { TokenPayload, TokenTypeEnum } from "src/app/interfaces";
import { UnauthorizedPageParams } from "src/app/pages/unauthorized/unauthorized.page";
import { HostBridgeService } from "src/app/services/host-bridge/host-bridge.service";

@Injectable({ providedIn: "root" })
export class AuthInterceptor implements HttpInterceptor {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly navController = inject(NavController);
  // Resolved on a 401, not at construction: the host bridge depends on translations, whose loader goes through this interceptor.
  private readonly injector = inject(Injector);

  private readonly url$: Observable<string> = this.store.select(FromRouter.selectUrl);
  private readonly programSlug$: Observable<string> = this.store.select(FromRouter.selectProgramSlug);

  private readonly authToken$: Observable<string> = this.store.select(FromUser.selectAuthToken);
  private readonly downloadToken$: Observable<string> = this.store.select(FromUser.selectDownloadToken);
  private readonly verifyToken$: Observable<string> = this.store.select(FromUser.selectVerifyToken);
  constructor() {}

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return combineLatest([this.authToken$, this.downloadToken$, this.verifyToken$, this.programSlug$, this.url$]).pipe(
      first(),
      switchMap(([authToken, downloadToken, verifyToken, slug, url]) => {
        let tokenToUse: string | null = authToken;

        if (req.url.endsWith("/apple/pass") || req.url.endsWith("/google/pass")) {
          tokenToUse = downloadToken || authToken;
        } else if (req.url.endsWith("/validate")) {
          tokenToUse = verifyToken;
        }

        return this.handle(req, next, tokenToUse, slug, url, !!tokenToUse && tokenToUse === authToken);
      }),
      catchError(error => throwError(() => error)),
    );
  }

  private handle(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    tokenToUse: string | null,
    slug: string,
    url: string,
    canRenewToken: boolean,
  ): Observable<HttpEvent<unknown>> {
    if (tokenToUse) req = req.clone({ setHeaders: { authorization: `Bearer ${tokenToUse}` } });

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status >= HttpStatusCode.InternalServerError) Sentry.captureException(error);

        if (error.status === HttpStatusCode.Unauthorized) {
          if (canRenewToken) {
            return this.authToken$.pipe(
              first(),
              switchMap(latestToken => {
                // A concurrent request may have already renewed the token while this one was in flight.
                if (latestToken && latestToken !== tokenToUse) return this.handle(req, next, latestToken, slug, url, false);

                return this.injector.get(HostBridgeService).requestToken().pipe(
                  switchMap(hostToken => {
                    const renewedToken = this.toAuthToken(hostToken);
                    if (!renewedToken) {
                      this.logout(error, tokenToUse, slug, url);
                      return throwError(() => error);
                    }

                    this.store.dispatch(UserActions.setAuthToken({ token: renewedToken }));
                    return this.handle(req, next, renewedToken, slug, url, false);
                  }),
                );
              }),
            );
          }

          this.logout(error, tokenToUse, slug, url);
        }
        return throwError(() => error);
      }),
    );
  }

  /** Same validation as the auth page: only auth/pass tokens can become the session token. */
  private toAuthToken(token: string | null): string | null {
    if (!token) return null;
    try {
      const { type } = jwtDecode<TokenPayload>(token);
      return [TokenTypeEnum.auth, TokenTypeEnum.pass].includes(type) ? token : null;
    } catch {
      return null;
    }
  }

  private logout(error: HttpErrorResponse, tokenToUse: string | null, slug: string, url: string): void {
    this.store.dispatch(
      UserActions.logout({
        callback: () => {
          localStorage.clear();

          if (url && !url.includes(AppRoutes.unauthorized)) {
            if (error?.error?.message?.includes("DOWNLOAD_TOKEN_EXPIRED")) {
              let email: string;
              try {
                const payload = jwtDecode<TokenPayload>(tokenToUse);
                if (payload.type === TokenTypeEnum.download) this.store.dispatch(UserActions.setDownloadToken({ token: "" }));
                email = payload.email;
              } catch {}
              this.navController.navigateRoot([slug, AppRoutes.unauthorizedDownload], { queryParams: { email } });
            } else {
              try {
                const payload = jwtDecode<TokenPayload>(tokenToUse);
                if ([TokenTypeEnum.auth, TokenTypeEnum.pass].includes(payload.type)) this.store.dispatch(UserActions.setAuthToken({ token: "" }));
                else if (TokenTypeEnum.verify === payload.type) this.store.dispatch(UserActions.setVerifyToken({ token: "" }));
              } catch {}

              let queryParams: UnauthorizedPageParams = {};
              try {
                const [, identifier, serial_number] = error?.error?.message?.split(".");
                queryParams = {...queryParams, identifier, serial_number};
              } catch {}
              this.navController.navigateRoot([slug, AppRoutes.unauthorized], { queryParams });
            }
          }
        },
      }),
    );
  }
}
